import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  FormControl,
  FormLabel,
  HStack,
  Select,
  VStack,
  Text,
  Divider,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  RadioGroup,
  Radio,
  Stack,
  useToast,
} from "@chakra-ui/react";
import { getSupabase, supabaseSchema } from "../components/supabaseClient";
import {
  prompts,
  tableName,
  colid2,
  BASE_URLS,
  getBackendURL,
} from "../components/env";
import PatientSuggestionDropdown from "./PatientSuggestionDropdown";
import { normalizeRole, isInGroup, getLabel } from "../config/roles";
import { stripNotesTableClientOnlyFields } from "./EditPatientModal";
import { splitPatientNameParts } from "../utils/patientNameParts";
import { minuteOptions, to12HourParts } from "../utils/timePickerParts";
import { useTemplateOptions } from "../hooks/medstudent/useTemplateOptions";
import { withStaleOption } from "../pages/medstudent/templateCatalog";
import { scheduleConfirmationForPatient } from "../utils/confirmationScheduling";

const AddPatientModal = ({ isOpen, onClose, onAddPatient, initialData, userRole }) => {
  const toast = useToast();
  // Initialize formData with defaults from prompts array
  const initialFormData = Object.fromEntries(
    prompts.map(([key, _, __, defaultValue]) => {
      // Set default document template type for tenant roles.
      const roleDefaultTemplateType = getLabel(userRole, 'templateType');
      if (key === "type" && roleDefaultTemplateType) {
        return [key, roleDefaultTemplateType];
      }
      // Clear physician_name default for non-clinical roles
      if (key === "physician_name" && !isInGroup(userRole, 'clinical')) {
        return [key, ""];
      }
      return [key, defaultValue || ""];
    }),
  );

  // Format time as 24-hour HH:mm and get today's date
  const now = new Date();
  const formattedTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  const formattedDate = now.toISOString().split("T")[0]; // Format: YYYY-MM-DD

  const [formData, setFormData] = useState({
    ...initialFormData,
    date_: formattedDate, // Set today as default date
    time_: "09:00:00", // Default 9 AM in 24h — picker keeps formData in sync
  });

  // Add state for time components
  const [timeComponents, setTimeComponents] = useState({
    hours: "09",
    minutes: "00",
    ampm: "AM",
  });

  // Add state for name components
  const [nameComponents, setNameComponents] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
  });

  // Add state for patient suggestion
  const [showPatientSuggestion, setShowPatientSuggestion] = useState(false);
  const [suggestedPatientName, setSuggestedPatientName] = useState("");

  // Add state for recurring schedules (freemium feature)
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringWeeks, setRecurringWeeks] = useState(8);
  const [recurringDays, setRecurringDays] = useState([]); // JS day numbers: 0=Sun,1=Mon,...,6=Sat
  const [notesBehavior, setNotesBehavior] = useState("accumulate");

  const toggleRecurringDay = (day) => {
    setRecurringDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  // Get the name field key from the first prompt
  const nameFieldKey = prompts[0][0];

  const [loading, setLoading] = useState(false);

  // FFP/FFS are always offered here: you are often scheduling a future visit,
  // so whether a populated prior document will exist by then is unknowable,
  // and filtering on today's data would hide a valid choice.
  const { options: templateOptions } = useTemplateOptions({ includeFollowUps: true });

  // Initialize form data when modal opens or initialData changes
  useEffect(() => {
    console.log("📅 AddPatientModal: Modal opened, initialData:", initialData);
    console.log(
      "📅 AddPatientModal: Current selectedDate from Schedules:",
      initialData?.date_,
    );
    if (isOpen) {
      if (initialData) {
        console.log(
          "📅 AddPatientModal: Setting form data with initialData date:",
          initialData.date_,
        );
        setFormData((prev) => ({
          ...prev,
          ...initialData,
        }));
        // The First/Middle/Last boxes are separate required inputs. Prefilling
        // only the composed name (as appointment approval does) left both
        // red-starred fields empty, so split it here too.
        const seededName = initialData[prompts[0][0]] || initialData.patient_name;
        if (seededName) {
          setNameComponents(splitPatientNameParts(seededName));
        }
        // Same trap as the name: setting formData.time_ alone left the PICKER
        // on its 09:00 AM default, so an approved 2:30 PM request opened as
        // 9 AM — and touching any select then wrote that back over the real
        // time.
        const seededTime = to12HourParts(initialData.time_);
        if (seededTime) setTimeComponents(seededTime);
      } else {
        // Reset to default values when modal opens without initialData
        console.log(
          "📅 AddPatientModal: No initialData, using default date:",
          formattedDate,
        );
        setTimeComponents({ hours: "09", minutes: "00", ampm: "AM" });
        setFormData((prev) => ({
          ...prev,
          date_: formattedDate,
          time_: "09:00:00",
        }));
      }
    } else {
      // Reset suggestion state when modal closes
      setSuggestedPatientName("");
      setShowPatientSuggestion(false);
    }
  }, [isOpen, initialData]);

  // Function to search for last visit
  const searchLastVisit = async (patientName) => {
    const supabase = await getSupabase();
    try {
      const { data, error } = await supabase
        .from(tableName)
        .select("*")
        .eq(colid2, patientName)
        .order("created_at", { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0) {
        const lastVisit = data[0];
        // Update form data with last visit data, excluding audio files, transcriptions, forms, and reports
        // forms_ind, reports, submissions_array, and submission_url should start fresh for each new schedule entry
        console.log('[AddPatientModal] searchLastVisit: raw last visit record from DB:', lastVisit);
        const {
          more_audios,
          created_at,
          id,
          transcripts,
          forms_ind,
          reports,
          submissions_array,
          submission_url,
          // See handlePatientSelect: don't carry the prior row's source zone into
          // a new appointment; the notes-c trigger stamps the current creator's zone.
          timezone,
          ...lastVisitWithoutAudio
        } = lastVisit;
        console.log('[AddPatientModal] searchLastVisit: stripped fields (should NOT carry over) =>', { forms_ind, reports, submissions_array, submission_url, more_audios });
        console.log('[AddPatientModal] searchLastVisit: remaining fields being spread into formData =>', lastVisitWithoutAudio);
        // Determine 24h time to use: from last visit if available, else default
        // The minutes used to be hardcoded to "00" in BOTH the picker and the
        // stored value, so a prior visit at 14:30 reopened as 2:00 PM and
        // saved back as 14:00. to12HourParts handles the 24h and "2:30 PM"
        // forms that notes-c holds, so the AM/PM special-casing that lived
        // here is gone with it.
        let resolvedTime24 = "09:00:00";
        let resolvedTC = { hours: "09", minutes: "00", ampm: "AM" };
        const priorTC = to12HourParts(lastVisit.time_);
        if (priorTC) {
          resolvedTC = priorTC;
          resolvedTime24 = to24h(priorTC.hours, priorTC.minutes, priorTC.ampm);
        }
        setTimeComponents(resolvedTC);

        setFormData((prev) => ({
          ...prev,
          ...lastVisitWithoutAudio,
          date_: initialData?.date_ || formattedDate, // Use initialData date if provided, otherwise today's date
          time_: resolvedTime24,
        }));

        // Update name components
        setNameComponents(splitPatientNameParts(lastVisit[prompts[0][0]]));
      }
    } catch (error) {
      console.error("Error searching for last visit:", error);
    }
  };

  // Handles input changes
  const handleChange = (key, value) => {
    // Ensure date format is correct for date inputs
    if (key === "dob" && value) {
      // Convert to YYYY-MM-DD format if needed
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        value = date.toISOString().split("T")[0];
      }
    }
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Helper: convert 12h picker components to 24h string "HH:MM:00"
  const to24h = (hours, minutes, ampm) => {
    let h = parseInt(hours, 10);
    if (ampm === "PM" && h !== 12) h += 12;
    if (ampm === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${minutes}:00`;
  };

  // Update time handler
  const handleTimeChange = (type, value) => {
    const newTimeComponents = {
      ...timeComponents,
      [type]: value,
    };
    setTimeComponents(newTimeComponents);
    handleChange("time_", to24h(newTimeComponents.hours, newTimeComponents.minutes, newTimeComponents.ampm));
  };

  // Handle patient selection from dropdown
  const handlePatientSelect = (selectedPatient) => {
    console.log(
      "📅 AddPatientModal: Patient selected from dropdown:",
      selectedPatient,
    );

    // Parse the selected patient's name. Either source can be undefined, which
    // the old inline .includes() would have thrown on.
    const fullName =
      selectedPatient.displayName || selectedPatient[nameFieldKey];
    const { firstName, middleName, lastName } = splitPatientNameParts(fullName);
    setNameComponents({ firstName, middleName, lastName });

    // Update form data with selected patient's information, but preserve the current date and time
    // Exclude forms_ind, reports, submissions_array, and submission_url so new schedule entries start fresh
    console.log('[AddPatientModal] handlePatientSelect: raw selected patient record:', selectedPatient);
    const {
      more_audios,
      created_at,
      id,
      transcripts,
      date_,
      time_,
      forms_ind,
      reports,
      submissions_array,
      submission_url,
      // Source zone belongs to whoever CREATED the original row; a new appointment
      // is created by the current user, so let the notes-c trigger stamp their zone.
      timezone,
      // Confirmation state belongs to the ORIGINAL appointment. Carrying it over
      // would create the new appointment already "Confirmed" (or already asked),
      // so it would never be texted and the Status badge would lie.
      confirmation_status,
      confirmation_sent_at,
      confirmation_updated_at,
      confirmation_source,
      confirmation_updated_by,
      confirmation_rules_sent,
      ...patientDataWithoutAudio
    } = selectedPatient;
    console.log('[AddPatientModal] handlePatientSelect: stripped fields (should NOT carry over) =>', { forms_ind, reports, submissions_array, submission_url, more_audios });
    console.log('[AddPatientModal] handlePatientSelect: remaining fields being spread into formData =>', patientDataWithoutAudio);
    // Sync time from selected patient in 24h format
    let selectedTime24 = "09:00:00";
    let selectedTC = { hours: "09", minutes: "00", ampm: "AM" };
    if (selectedPatient.time_) {
      let [timeStr, ampm] = selectedPatient.time_.split(" ");
      let [hours, minutes] = timeStr.split(":");
      const h = parseInt(hours, 10);
      const ampmResolved = ampm || (h >= 12 ? "PM" : "AM");
      selectedTC = { hours: String(h % 12 || 12).padStart(2, "0"), minutes: "00", ampm: ampmResolved };
      let h24 = h;
      // Only apply AM/PM conversion when the original string had an explicit suffix.
      if (ampm) {
        if (ampm === "PM" && h24 !== 12) h24 += 12;
        if (ampm === "AM" && h24 === 12) h24 = 0;
      }
      selectedTime24 = `${String(h24).padStart(2, "0")}:00:00`;
    }
    setTimeComponents(selectedTC);
    setFormData((prev) => ({
      ...prev,
      ...patientDataWithoutAudio,
      date_: initialData?.date_ || formattedDate,
      time_: selectedTime24,
    }));

    // Hide the suggestion dropdown
    setShowPatientSuggestion(false);
  };

  // Handle patient name suggestion input
  const handlePatientNameSuggestion = (value) => {
    setSuggestedPatientName(value);
    if (value.trim().length >= 2) {
      setShowPatientSuggestion(true);
    } else {
      setShowPatientSuggestion(false);
    }
  };

  // Modify handleNameChange to trigger last visit search
  const handleNameChange = (type, value) => {
    const names = { ...nameComponents, [type]: value };
    setNameComponents(names);

    // Combine names in "Last, First Middle" format
    const fullName = [
      names.lastName, // Last name first
      [names.firstName, names.middleName].filter(Boolean).join(" "), // First and Middle together
    ]
      .filter(Boolean)
      .join(", "); // Join with comma

    // Update the name field using the key from prompts
    handleChange(nameFieldKey, fullName);

    // If we have both first and last name, search for last visit
    if (names.firstName && names.lastName) {
      searchLastVisit(fullName);
    }
  };

  // Handles form submission and database insertion
  const handleSubmit = async () => {
    console.log('[AddPatientModal] handleSubmit - userRole:', userRole);
    console.log('[AddPatientModal] handleSubmit - formData:', formData);

    const supabase = await getSupabase();
    // Check required fields from prompts array, excluding type field
    let requiredFields = prompts
      .filter(([key, _, required]) => required === "Required" && key !== "type")
      .map(([key]) => key);

    // For freemium users (students/social workers), DOB and EHR_ID are NOT required
    if (isInGroup(userRole, 'freemium')) {
      console.log('[AddPatientModal] Freemium user detected - removing DOB and EHR_ID from required fields');
      requiredFields = requiredFields.filter(field => field !== 'dob' && field !== 'EHR_ID');
    }

    console.log('[AddPatientModal] Required fields:', requiredFields);
    const missingFields = requiredFields.filter((field) => !formData[field]);
    console.log('[AddPatientModal] Missing fields:', missingFields);

    if (missingFields.length > 0 || !formData.date_ || !formData.time_) {
      alert(`Please fill in all required fields: ${missingFields.join(", ")}`);
      return;
    }

    setLoading(true);

    try {
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error("User not authenticated.");

      // Format time properly before sending to Supabase
      const formattedTime =
        typeof formData.time_ === "object"
          ? `${formData.time_.hour}:${formData.time_.minute} ${formData.time_.ampm}`
          : formData.time_;

      // Create a new patient entry, excluding id, created_at, and suggestion-only fields
      const {
        id,
        created_at,
        displayName,
        lastVisit,
        visitCount,
        ...patientData
      } = formData;

      // Auto-generate 6-digit ID for freemium users if EHR_ID is empty
      if (isInGroup(userRole, 'freemium') && (!patientData.EHR_ID || patientData.EHR_ID.trim() === '')) {
        // Generate a random 6-digit number
        const generatedID = Math.floor(100000 + Math.random() * 900000).toString();
        patientData.EHR_ID = generatedID;
        // Update formData so it's retained for subsequent edits
        setFormData(prev => ({
          ...prev,
          EHR_ID: generatedID
        }));
        console.log(`[AddPatientModal] Auto-generated ${getLabel(userRole, 'idField')}:`, generatedID);
      }

      // Filter out empty optional fields and handle freemium user fields
      const filteredData = Object.entries({
        ...patientData,
        time_: formattedTime,
        user_id: userId,
        notes_behavior: notesBehavior,
      }).filter(([key, value]) => {
        // For freemium users, exclude certain fields entirely if empty
        if (isInGroup(userRole, 'freemium')) {
          // These fields don't apply to students/social workers
          // Note: EHR_ID is now auto-generated if empty, so it shouldn't be excluded
          if (key === 'dob' || key === 'tel_num' || key === 'home_phone') {
            if (!value || value === '') {
              console.log(`[AddPatientModal] Excluding ${key} for freemium user (empty value)`);
              return false;
            }
          }
        }

        // Keep required fields and non-empty optional fields
        const isRequired =
          prompts.find((p) => p[0] === key)?.[2] === "Required";
        return (
          isRequired ||
          (value !== null && value !== undefined && value !== "")
        );
      });

      const newPatient = Object.fromEntries(filteredData);
      console.log('[AddPatientModal] Filtered patient data:', newPatient);

      // Handle recurring schedules for freemium users
      let entriesToInsert = [newPatient];

      if (isRecurring && isInGroup(userRole, 'freemium')) {
        // Parse date as local YYYY-MM-DD to avoid UTC shift
        const [by, bm, bd] = newPatient.date_.split("-").map(Number);
        const baseDate = new Date(by, bm - 1, bd);
        const baseDayOfWeek = baseDate.getDay();

        // Default to just the base date's day if user didn't select any
        const activeDays = recurringDays.length > 0 ? recurringDays : [baseDayOfWeek];
        console.log(`📅 AddPatientModal: Recurring pattern — days: [${activeDays}], weeks: ${recurringWeeks}`);

        activeDays.forEach(targetDay => {
          // Find first occurrence of targetDay on or after baseDate
          let diff = targetDay - baseDayOfWeek;
          if (diff < 0) diff += 7;
          const firstOcc = new Date(baseDate);
          firstOcc.setDate(baseDate.getDate() + diff);

          // If this is the base date's day, week 0 is already the base entry — start at week 1
          const startWeek = diff === 0 ? 1 : 0;

          for (let week = startWeek; week < recurringWeeks; week++) {
            const nextDate = new Date(firstOcc);
            nextDate.setDate(firstOcc.getDate() + week * 7);
            const nextDateString = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, "0")}-${String(nextDate.getDate()).padStart(2, "0")}`;
            entriesToInsert.push({ ...newPatient, date_: nextDateString });
          }
        });

        // Sort all entries chronologically
        entriesToInsert.sort((a, b) => a.date_.localeCompare(b.date_));
        console.log(`📅 AddPatientModal: Total entries to insert: ${entriesToInsert.length}`);

        // notes_behavior is already saved per-row in the DB — no localStorage needed
      }

      // Insert into Supabase
      console.log(
        "📅 AddPatientModal: Inserting entries:",
        entriesToInsert.map(e => ({ date: e.date_, time: e.time_ })),
      );
      console.log('[AddPatientModal] PRE-INSERT forms/reports check on first entry =>', {
        forms_ind: entriesToInsert[0]?.forms_ind,
        reports: entriesToInsert[0]?.reports,
        submissions_array: entriesToInsert[0]?.submissions_array,
        submission_url: entriesToInsert[0]?.submission_url,
        full_entry: entriesToInsert[0],
      });
      // Strip client-only fields (e.g. `_schema`/`_source` from the cross-schema
      // suggestion fan-out, plus display-only fields) — they aren't notes-c
      // columns and Postgres rejects the insert if they're present.
      const cleanedEntries = entriesToInsert.map(stripNotesTableClientOnlyFields);
      const { data, error } = await supabase
        .from(tableName)
        .insert(cleanedEntries)
        .select();
      if (error) throw error;
      console.log(
        "📅 AddPatientModal: Entries inserted successfully:",
        data.length,
        "entries",
      );

      // Schedule auto-reminder for 1 day before appointment
      try {
        const hostname = window.location.hostname;
        const baseUrl =
          hostname === "localhost"
            ? BASE_URLS.local.backend
            : BASE_URLS.render.backend;
        const backendUrl = `${baseUrl}/api/reminders/schedule-auto-reminder/`;

        console.log(
          "📅 AddPatientModal: Scheduling auto-reminder for patient:",
          data[0].id,
        );

        const reminderResponse = await fetch(backendUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            patient_id: data[0].id,
            appointment_date: newPatient.date_,
            appointment_time: formattedTime,
            schema: supabaseSchema,
          }),
        });

        if (reminderResponse.ok) {
          const reminderData = await reminderResponse.json();
          console.log(
            "📅 AddPatientModal: Auto-reminder scheduling result:",
            reminderData,
          );
        } else {
          console.warn(
            "📅 AddPatientModal: Failed to schedule auto-reminder:",
            reminderResponse.status,
          );
        }
      } catch (reminderError) {
        console.warn(
          "📅 AddPatientModal: Error scheduling auto-reminder:",
          reminderError.message,
        );
        // Don't fail the whole operation if reminder scheduling fails
      }

      // Add all new entries to the list. Awaited in order: the Admin page's
      // onAddPatient sends the welcome message, and when that welcome asked
      // the patient to confirm (appointment < 7 days away) the confirmation
      // hook below must see it, or it would warn staff to send a manual
      // confirmation the welcome just asked for.
      for (const entry of data) {
        await onAddPatient(entry);
      }
      for (const entry of data) {
        await scheduleConfirmationForPatient({
          schema: supabaseSchema,
          notesCId: entry?.id,
          toast,
        });
      }

      // Reset form fields
      setFormData({ name: "", email: "", date_: "" });
      setIsRecurring(false);
      setRecurringWeeks(8);
      setRecurringDays([]);
      onClose(); // Close modal
    } catch (error) {
      console.error(`Error adding ${getLabel(userRole, 'entity').toLowerCase()}:`, error.message);
      alert(`Failed to add ${getLabel(userRole, 'entity').toLowerCase()}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add New {getLabel(userRole, 'entity')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Patient/Class/Meeting Suggestion Section */}
          <VStack spacing={4} align="stretch" mb={6}>
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Quick Add: Search for existing {getLabel(userRole, 'entity').toLowerCase()}s to auto-fill information
              </Text>
              <FormControl>
                <FormLabel fontSize="sm">Search Existing {getLabel(userRole, 'entity')}s</FormLabel>
                <PatientSuggestionDropdown
                  value={suggestedPatientName}
                  onChange={handlePatientNameSuggestion}
                  onPatientSelect={handlePatientSelect}
                  placeholder={`Type ${getLabel(userRole, 'entity').toLowerCase()} name to search...`}
                  entityName={getLabel(userRole, 'entity')}
                  // "Last visit" = latest visit before the schedule date this
                  // modal is adding for (the date you're on in Schedules).
                  referenceDate={initialData?.date_ || formattedDate}
                />
              </FormControl>
              <Divider />
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Or manually enter new {getLabel(userRole, 'entity').toLowerCase()} information:
              </Text>
            </VStack>

          {/* Name Fields - Different for students vs others */}
          {normalizeRole(userRole) === 'student' ? (
            <FormControl isRequired mt={4}>
              <FormLabel>{getLabel(userRole, 'nameField')}</FormLabel>
              <Input
                value={formData[nameFieldKey] || ""}
                onChange={(e) => handleChange(nameFieldKey, e.target.value)}
                placeholder="Enter class name"
              />
            </FormControl>
          ) : (
            <>
              {/* Name Fields in one row */}
              <HStack spacing={4} mt={4}>
                <FormControl isRequired>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    value={nameComponents.firstName}
                    onChange={(e) => handleNameChange("firstName", e.target.value)}
                    placeholder="First Name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Middle Name</FormLabel>
                  <Input
                    value={nameComponents.middleName}
                    onChange={(e) => handleNameChange("middleName", e.target.value)}
                    placeholder="Middle Name"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    value={nameComponents.lastName}
                    onChange={(e) => handleNameChange("lastName", e.target.value)}
                    placeholder="Last Name"
                  />
                </FormControl>
              </HStack>

              {/* Read-only display of formatted name */}
              <FormControl mt={4}>
                <FormLabel>Name (as will appear)</FormLabel>
                <Input
                  value={formData[nameFieldKey] || ""}
                  isReadOnly
                  bg="gray.50"
                  placeholder="Last, First Middle"
                />
              </FormControl>
            </>
          )}

          {/* Rest of the form fields */}
          {prompts
            .filter(([key]) => {
              // Filter out name field (handled above)
              if (key === nameFieldKey) return false;

              // For freemium users, exclude these fields
              if (isInGroup(userRole, 'freemium')) {
                if (key === "dob") return false;  // No DOB
                if (key === "tel_num") return false;  // Phone optional - exclude it
                if (key === "home_phone") return false;
              }

              return true;
            })
            .map(([key, label, required]) => {
              // Adjust labels for role-specific fields
              let displayLabel = label;
              if (key === "EHR_ID") {
                displayLabel = getLabel(userRole, 'idField');
              } else if (key === "physician_name") {
                displayLabel = getLabel(userRole, 'physicianField');
              } else if (key === "email") {
                // Email is optional for freemium users
                required = isInGroup(userRole, 'freemium') ? "Optional" : required;
              }

              return (
                <FormControl
                  key={key}
                  isRequired={required === "Required"}
                  mt={4}
                >
                  <FormLabel>{displayLabel}</FormLabel>
                  {key === "dob" ? (
                    <Input
                      type="date"
                      value={formData[key] || ""}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={`Enter ${displayLabel}`}
                    />
                  ) : (
                    <Input
                      value={formData[key] || ""}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={`Enter ${displayLabel}`}
                    />
                  )}
                </FormControl>
              );
            })}

          {/* Optional per-row template override. Takes priority over Type when
              set. Deliberately not part of `prompts`, which renders plain text
              inputs and drives required-field validation. */}
          <FormControl mt={4}>
            <FormLabel>Template</FormLabel>
            <Select
              value={formData.template || ""}
              onChange={(e) => handleChange("template", e.target.value)}
              placeholder="Use type"
            >
              {withStaleOption(templateOptions, formData.template).map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </Select>
            <Text fontSize="xs" color="gray.600" mt={1}>
              Optional. Overrides the Type field when choosing this document's template.
            </Text>
          </FormControl>

          {/* Time Input Field with scroll selector */}
          <FormControl isRequired mt={4}>
            <FormLabel>Time</FormLabel>
            <HStack>
              {/* Hours */}
              <Select
                value={timeComponents.hours}
                onChange={(e) => handleTimeChange("hours", e.target.value)}
                width="70px"
              >
                {Array.from({ length: 12 }).map((_, i) => (
                  <option key={i + 1} value={String(i + 1).padStart(2, "0")}>
                    {String(i + 1).padStart(2, "0")}
                  </option>
                ))}
              </Select>

              {/* Minutes in 15-minute intervals */}
              <Select
                value={timeComponents.minutes}
                onChange={(e) => handleTimeChange("minutes", e.target.value)}
                width="70px"
              >
                {minuteOptions(timeComponents.minutes).map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>

              {/* AM/PM */}
              <Select
                value={timeComponents.ampm}
                onChange={(e) => handleTimeChange("ampm", e.target.value)}
                width="70px"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </Select>
            </HStack>
          </FormControl>

          {/* Date Input Field */}
          <FormControl isRequired mt={4}>
            <FormLabel>Select Date</FormLabel>
            <Input
              type="date"
              value={formData.date_}
              onChange={(e) => handleChange("date_", e.target.value)}
            />
          </FormControl>

          {/* Recurring Schedule Option (Freemium users only) */}
          {isInGroup(userRole, 'freemium') && (
            <VStack align="stretch" mt={4} spacing={3} p={4} bg="blue.50" borderRadius="md">
              <Checkbox
                isChecked={isRecurring}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsRecurring(checked);
                  if (checked && formData.date_) {
                    const [y, m, d] = formData.date_.split("-").map(Number);
                    setRecurringDays([new Date(y, m - 1, d).getDay()]);
                  } else {
                    setRecurringDays([]);
                  }
                }}
                colorScheme="blue"
              >
                <Text fontWeight="medium">
                  Make this a recurring {getLabel(userRole, 'entity').toLowerCase()}
                </Text>
              </Checkbox>

              {isRecurring && (
                <>
                  <FormControl>
                    <FormLabel fontSize="sm">Days of week</FormLabel>
                    <HStack spacing={3} flexWrap="wrap">
                      {[
                        { label: "Su", value: 0 },
                        { label: "M",  value: 1 },
                        { label: "Tu", value: 2 },
                        { label: "W",  value: 3 },
                        { label: "Th", value: 4 },
                        { label: "F",  value: 5 },
                        { label: "Sa", value: 6 },
                      ].map(({ label, value }) => (
                        <Checkbox
                          key={value}
                          isChecked={recurringDays.includes(value)}
                          onChange={() => toggleRecurringDay(value)}
                          colorScheme="blue"
                          size="sm"
                        >
                          {label}
                        </Checkbox>
                      ))}
                    </HStack>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Number of weeks</FormLabel>
                    <NumberInput
                      value={recurringWeeks}
                      onChange={(valueString) => setRecurringWeeks(parseInt(valueString) || 1)}
                      min={1}
                      max={52}
                      size="sm"
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    {(() => {
                      const dayNames = ["Su", "M", "Tu", "W", "Th", "F", "Sa"];
                      const getBaseDayOfWeek = () => {
                        if (!formData.date_) return -1;
                        const [y, m, d] = formData.date_.split("-").map(Number);
                        return new Date(y, m - 1, d).getDay();
                      };
                      const baseDayOfWeek = getBaseDayOfWeek();
                      const activeDays = recurringDays.length > 0 ? recurringDays : (
                        baseDayOfWeek >= 0 ? [baseDayOfWeek] : []
                      );
                      const baseDayIncluded = activeDays.includes(baseDayOfWeek);
                      const totalEntries = baseDayIncluded
                        ? activeDays.length * recurringWeeks
                        : 1 + activeDays.length * recurringWeeks;
                      const pattern = [...activeDays].sort((a, b) => a - b).map(d => dayNames[d]).join("/");
                      return (
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          Pattern: <strong>{pattern || "—"}</strong> &mdash; {totalEntries} total entries
                          over {recurringWeeks} week{recurringWeeks !== 1 ? "s" : ""},
                          starting {formData.date_ || "the selected date"}
                        </Text>
                      );
                    })()}
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Notes behavior each session</FormLabel>
                    <RadioGroup value={notesBehavior} onChange={setNotesBehavior}>
                      <Stack direction="row" spacing={4}>
                        <Radio value="accumulate" colorScheme="blue" size="sm">
                          Carry over notes
                        </Radio>
                        <Radio value="fresh" colorScheme="blue" size="sm">
                          Start fresh
                        </Radio>
                      </Stack>
                    </RadioGroup>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      {notesBehavior === "accumulate"
                        ? `When opening an empty session, you'll be offered the option to copy notes from the previous session.`
                        : "Each session will open with a blank template."}
                    </Text>
                  </FormControl>
                </>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" onClick={handleSubmit} isLoading={loading}>
            Add {getLabel(userRole, 'entity')}
          </Button>
          <Button onClick={onClose} ml={3}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddPatientModal;
